import { CheckmarkCircle01Icon, LinkSquare02Icon, StarIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Paper,
  Radio,
  RadioGroup,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { resolveSurveyStatus, useSubmitSurveyResponseMutation } from "@/api/surveysApi";
import { useFetchOrganizationsByIdQuery } from "@/features/organizations/api/organizationsApi";
import { useAppSelector } from "@/hooks";
import useSnackbar from "@/hooks/useSnackbar";
import { Question, Survey } from "@/types/surveys";
import {
  buildGoogleWriteReviewUrl,
  formatGoogleReviewClipboardText,
  resolveSurveyGoogleReviewsDefaults,
} from "./SurveyGoogleReviewsDialog";

interface SurveyResponseFormProps {
  survey: Survey;
  organizationId: string;
  orderId?: string | null;
  orderNumber?: string | number | null;
  branchId?: string | null;
  tableId?: string | null;
  flow?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  clientPhone?: string | null;
  onSubmitted?: () => void;
  onCancel?: () => void;
}

const SurveyResponseForm = ({
  survey,
  organizationId,
  orderId,
  orderNumber,
  branchId,
  tableId,
  flow,
  clientId,
  clientName,
  clientPhone,
  onSubmitted,
  onCancel,
}: SurveyResponseFormProps) => {
  const [openSnackbar] = useSnackbar();
  const [answers, setAnswers] = useState<Record<string, string | string[] | number>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [copiedFeedback, setCopiedFeedback] = useState(false);

  const reduxOrg = useAppSelector((state) =>
    organizationId ? state.organizations?.[organizationId] : null
  );
  const orgQuery = useFetchOrganizationsByIdQuery(organizationId || skipToken, {
    skip: Boolean(reduxOrg) || !organizationId,
  });
  const organization = reduxOrg || orgQuery.data;

  const existingPlaceId = useMemo(
    () => (organization?.googlePlaceId || "").trim(),
    [organization?.googlePlaceId]
  );

  const { ratingQuestions, resolvedConfig } = useMemo(
    () => resolveSurveyGoogleReviewsDefaults(survey),
    [survey]
  );

  const hasRatingWidget = ratingQuestions.length > 0;
  const selectedRatingQuestionId = resolvedConfig.ratingQuestionId ?? "";
  const selectedCommentQuestionId = resolvedConfig.commentQuestionId ?? "";

  const [submitResponse, submitState] = useSubmitSurveyResponseMutation();
  const surveyStatus = resolveSurveyStatus(survey);

  const selectedRatingValue = useMemo(() => {
    const raw = selectedRatingQuestionId ? answers[selectedRatingQuestionId] : undefined;
    const num = Number(raw);
    return !Number.isNaN(num) && num >= 1 && num <= 5 ? num : null;
  }, [answers, selectedRatingQuestionId]);

  const selectedCommentText = useMemo(() => {
    const raw = selectedCommentQuestionId ? answers[selectedCommentQuestionId] : undefined;
    return typeof raw === "string" ? raw : "";
  }, [answers, selectedCommentQuestionId]);

  const handleSingleChange = (questionId: string, value: string | number) => {
    setValidationError(null);
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleMultipleChange = (questionId: string, optionId: string, checked: boolean) => {
    setValidationError(null);
    setAnswers((prev) => {
      const current = Array.isArray(prev[questionId]) ? (prev[questionId] as string[]) : [];
      const updated = checked
        ? [...current, optionId]
        : current.filter((id) => id !== optionId);
      return {
        ...prev,
        [questionId]: updated,
      };
    });
  };

  const handleCopyAndRedirectToGoogle = async () => {
    if (!existingPlaceId) {
      return;
    }

    const clipboardText = formatGoogleReviewClipboardText(
      selectedRatingValue,
      selectedCommentText
    );

    if (clipboardText) {
      try {
        await navigator.clipboard.writeText(clipboardText);
        setCopiedFeedback(true);
        openSnackbar(
          "Reseña y puntaje copiados al portapapeles. ¡Pégalos en Google Reviews!",
          "success"
        );
      } catch {
        // Continuar con la redirección aunque el navegador bloquee el portapapeles
      }
    }

    window.open(
      buildGoogleWriteReviewUrl(existingPlaceId),
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Validar preguntas obligatorias
    for (const q of survey.questions ?? []) {
      const isRequired = q.required ?? true;
      if (!isRequired) continue;

      const val = answers[q.id];
      const isEmpty =
        val === undefined ||
        val === null ||
        val === "" ||
        (Array.isArray(val) && val.length === 0);

      if (isEmpty) {
        setValidationError(`Por favor responde: "${q.title}"`);
        return;
      }
    }

    try {
      await submitResponse({
        organizationId,
        surveyId: survey.id,
        survey,
        payload: {
          answers,
          orderId: orderId ?? null,
          orderNumber: orderNumber ?? null,
          branchId: branchId ?? null,
          tableId: tableId ?? null,
          flow: flow ?? null,
          clientId: clientId ?? null,
          clientName: clientName ?? null,
          clientPhone: clientPhone ?? null,
        },
      }).unwrap();

      setIsSubmitted(true);
      onSubmitted?.();
    } catch (err: any) {
      setValidationError(
        err?.message || "Ocurrió un error al enviar tu respuesta. Intenta nuevamente."
      );
    }
  };

  const meetsMinRating =
    selectedRatingValue === null ||
    selectedRatingValue >= (resolvedConfig.minRating ?? 1);

  if (isSubmitted) {
    return (
      <Stack spacing={3} alignItems="center" py={3} px={1} textAlign="center">
        <Stack spacing={1.5} alignItems="center">
          <HugeiconsIcon icon={CheckmarkCircle01Icon} size={48} color="#22c55e" />
          <Typography variant="h4" fontWeight={700}>
            ¡Muchas gracias por tu opinión!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tus respuestas nos ayudan a brindarte una mejor experiencia en cada pedido.
          </Typography>
        </Stack>

        {Boolean(existingPlaceId) &&
          hasRatingWidget &&
          resolvedConfig.enabled !== false &&
          meetsMinRating && (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                width: "100%",
                textAlign: "left",
                borderColor: "warning.light",
                bgcolor: "rgba(245, 158, 11, 0.04)",
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <HugeiconsIcon icon={StarIcon} size={20} fill="#f59e0b" color="#d97706" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    ¿Nos ayudas compartiendo tu reseña en Google?
                  </Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  Copiaremos tu comentario y puntaje automáticamente para que puedas publicarlo en
                  Google Reviews en un clic:
                </Typography>

                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fff" }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Tu calificación:
                      </Typography>
                      <Rating
                        size="small"
                        readOnly
                        value={selectedRatingValue ?? 5}
                      />
                      <Chip
                        size="small"
                        color="warning"
                        label={`${selectedRatingValue ?? 5}/5 ★`}
                      />
                    </Stack>
                    {selectedCommentText ? (
                      <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                        "{selectedCommentText}"
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Se copiará tu calificación ({selectedRatingValue ?? 5}/5 ★) al portapapeles.
                      </Typography>
                    )}
                  </Stack>
                </Paper>

                {copiedFeedback && (
                  <Alert severity="success" sx={{ py: 0.5 }}>
                    ¡Texto y puntaje copiados! Solo pégalos en la ventana de Google Reviews.
                  </Alert>
                )}

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="warning"
                    endIcon={<HugeiconsIcon icon={LinkSquare02Icon} size={16} />}
                    onClick={handleCopyAndRedirectToGoogle}
                  >
                    Compartir en Google Reviews
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          )}
      </Stack>
    );
  }

  if (surveyStatus === "paused") {
    return (
      <Alert severity="warning">
        Por el momento esta encuesta se encuentra en pausa y no puede ser llenada.
      </Alert>
    );
  }

  if (surveyStatus === "finished") {
    return (
      <Alert severity="error">
        Esta encuesta ha finalizado y ya no puede ser llenada.
      </Alert>
    );
  }

  const renderQuestionInput = (question: Question) => {
    const qType = question.type || "single_choice";
    const currentValue = answers[question.id];

    if (qType === "rating") {
      return (
        <Box sx={{ py: 1 }}>
          <Rating
            size="large"
            value={typeof currentValue === "number" ? currentValue : 0}
            onChange={(_, newValue) => {
              if (newValue !== null) {
                handleSingleChange(question.id, newValue);
              }
            }}
            emptyIcon={<HugeiconsIcon icon={StarIcon} style={{ opacity: 0.35 }} />}
          />
        </Box>
      );
    }

    if (qType === "single_choice") {
      return (
        <RadioGroup
          value={typeof currentValue === "string" ? currentValue : ""}
          onChange={(e) => handleSingleChange(question.id, e.target.value)}
        >
          {(question.options ?? []).map((opt, idx) => {
            const optId = opt.id || String(idx);
            return (
              <FormControlLabel
                key={optId}
                value={optId}
                control={<Radio size="small" />}
                label={opt.title}
              />
            );
          })}
        </RadioGroup>
      );
    }

    if (qType === "multiple_choice") {
      const selectedList = Array.isArray(currentValue) ? currentValue : [];
      return (
        <FormGroup>
          {(question.options ?? []).map((opt, idx) => {
            const optId = opt.id || String(idx);
            const isChecked = selectedList.includes(optId);
            return (
              <FormControlLabel
                key={optId}
                control={
                  <Checkbox
                    size="small"
                    checked={isChecked}
                    onChange={(e) =>
                      handleMultipleChange(question.id, optId, e.target.checked)
                    }
                  />
                }
                label={opt.title}
              />
            );
          })}
        </FormGroup>
      );
    }

    if (qType === "text") {
      return (
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder="Escribe tu comentario aquí..."
          value={typeof currentValue === "string" ? currentValue : ""}
          onChange={(e) => handleSingleChange(question.id, e.target.value)}
        />
      );
    }

    return null;
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2.5}>
        {survey.description ? (
          <Typography variant="body2" color="text.secondary">
            {survey.description}
          </Typography>
        ) : null}

        {validationError ? (
          <Alert severity="warning" onClose={() => setValidationError(null)}>
            {validationError}
          </Alert>
        ) : null}

        {(survey.questions ?? []).map((question, idx) => (
          <Paper
            key={question.id || `q-${idx}`}
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, backgroundColor: "#fff" }}
          >
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                {idx + 1}. {question.title}
                {(question.required ?? true) && (
                  <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                    *
                  </Typography>
                )}
              </Typography>
              {renderQuestionInput(question)}
            </Stack>
          </Paper>
        ))}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="flex-end"
        >
          {onCancel && (
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={submitState.isLoading}
            >
              Ahora no
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth={!onCancel}
            disabled={submitState.isLoading}
          >
            {submitState.isLoading ? "Enviando..." : "Enviar opinión"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default SurveyResponseForm;
